import React from 'react';
import {
  Typography,
  FormControl,
  TextField,
  makeStyles,
  Chip,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Avatar,
  IconButton,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Close } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import {
  Role,
  Permission,
  DetailedUser,
  UserRole,
  UserExtraPermission,
} from 'lib/Model/Permission';

const useStyles = makeStyles((theme) => ({
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  userAvatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    marginRight: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    fontSize: '1.5rem',
    fontWeight: 600,
  },
  userInfo: {
    flexGrow: 1,
  },
  userName: {
    marginBottom: theme.spacing(0.5),
  },
  userEmail: {
    color: theme.palette.text.secondary,
  },
  sectionContainer: {
    marginBottom: theme.spacing(4),
  },
  sectionHeader: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  roleItem: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
  },
  roleItemTitle: {
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
  },
  roleItemHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  roleItemActions: {
    marginLeft: theme.spacing(1),
  },
  roleItemDescription: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  roleItemDate: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  chip: {
    margin: theme.spacing(0.25),
    fontSize: '0.75rem',
  },
  alertBox: {
    marginTop: theme.spacing(2),
  },
  warningBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.warning.main}`,
  },
  dialogActions: {
    padding: theme.spacing(2),
    gap: theme.spacing(1),
  },
  noteField: {
    marginTop: theme.spacing(2),
  },
  superuserChip: {
    marginLeft: theme.spacing(1),
  },
}));

interface UserManagementDialogProps {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  selectedUser: DetailedUser | null;
  userRoles: UserRole[];
  userRolesLoading: boolean;
  userExtraPermissions: UserExtraPermission[];
  userExtraPermissionsLoading: boolean;
  userEffectivePermissions: string[];
  userEffectivePermissionsLoading: boolean;
  rolesData: Role[] | undefined;
  permissionsData: Permission[] | undefined;
  addRoleDialogOpen: boolean;
  setAddRoleDialogOpen: (open: boolean) => void;
  selectedRoleToAdd: Role | null;
  setSelectedRoleToAdd: (role: Role | null) => void;
  assigningRole: boolean;
  grantPermissionDialogOpen: boolean;
  setGrantPermissionDialogOpen: (open: boolean) => void;
  selectedPermissionToGrant: Permission | null;
  setSelectedPermissionToGrant: (permission: Permission | null) => void;
  permissionNote: string;
  setPermissionNote: (note: string) => void;
  grantingPermission: boolean;
  hasAdminRole: boolean;
  currentUserIsSuperuser: boolean;
  handleAssignRole: () => void;
  handleRemoveRole: (roleSlug: string) => void;
  handleGrantPermission: () => void;
  handleRevokePermission: (permissionSlug: string) => void;
}

const UserManagementDialog: React.FC<UserManagementDialogProps> = ({
  dialogOpen,
  setDialogOpen,
  selectedUser,
  userRoles,
  userRolesLoading,
  userExtraPermissions,
  userExtraPermissionsLoading,
  userEffectivePermissions,
  userEffectivePermissionsLoading,
  rolesData,
  permissionsData,
  addRoleDialogOpen,
  setAddRoleDialogOpen,
  selectedRoleToAdd,
  setSelectedRoleToAdd,
  assigningRole,
  grantPermissionDialogOpen,
  setGrantPermissionDialogOpen,
  selectedPermissionToGrant,
  setSelectedPermissionToGrant,
  permissionNote,
  setPermissionNote,
  grantingPermission,
  hasAdminRole,
  currentUserIsSuperuser,
  handleAssignRole,
  handleRemoveRole,
  handleGrantPermission,
  handleRevokePermission,
}) => {
  const classes = useStyles();

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <Box className={classes.dialogHeader}>
          <Avatar className={classes.userAvatar}>
            {selectedUser?.name_surname?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box className={classes.userInfo}>
            <Typography variant="h6" className={classes.userName}>
              {selectedUser?.name_surname}
            </Typography>
            <Typography variant="body2" className={classes.userEmail}>
              {selectedUser?.email_address}
            </Typography>
          </Box>
        </Box>
        <DialogContent>
          <Box className={classes.sectionContainer}>
            <Box className={classes.sectionHeader}>
              <Typography variant="h6">Assigned Roles</Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => setAddRoleDialogOpen(true)}
              >
                + ADD ROLE
              </Button>
            </Box>
            <Box className={classes.sectionContent}>
              {userRolesLoading ? (
                <Typography variant="body1">Loading roles...</Typography>
              ) : userRoles.length > 0 ? (
                userRoles.map((role) => (
                  <Box key={role.slug} className={classes.roleItem}>
                    <Box className={classes.roleItemHeader}>
                      <Typography
                        variant="h6"
                        className={classes.roleItemTitle}
                      >
                        {role.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveRole(role.slug)}
                        className={classes.roleItemActions}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography
                      variant="body2"
                      className={classes.roleItemDescription}
                    >
                      {role.description}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={classes.roleItemDate}
                    >
                      Assigned on:{' '}
                      {new Date(role.assigned_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body1">
                  No roles assigned to this user.
                </Typography>
              )}
            </Box>
          </Box>

          <Box className={classes.sectionContainer}>
            <Box className={classes.sectionHeader}>
              <Typography variant="h6">Extra Permissions</Typography>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={hasAdminRole}
                onClick={() => setGrantPermissionDialogOpen(true)}
              >
                + GRANT PERMISSION
              </Button>
            </Box>
            <Box className={classes.sectionContent}>
              {userExtraPermissionsLoading ? (
                <Typography variant="body1">Loading permissions...</Typography>
              ) : userExtraPermissions.length > 0 ? (
                userExtraPermissions.map((permission) => (
                  <Box
                    key={permission.permission_slug}
                    className={classes.roleItem}
                  >
                    <Box className={classes.roleItemHeader}>
                      <Typography
                        variant="h6"
                        className={classes.roleItemTitle}
                      >
                        {permission.permission_name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleRevokePermission(permission.permission_slug)
                        }
                        className={classes.roleItemActions}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                    {permission.note && (
                      <Typography
                        variant="body2"
                        className={classes.roleItemDescription}
                      >
                        {permission.note}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      className={classes.roleItemDate}
                    >
                      Granted on:{' '}
                      {new Date(permission.granted_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body1">
                  No extra permissions granted to this user.
                </Typography>
              )}
            </Box>
          </Box>

          <Box className={classes.sectionContainer}>
            <Box className={classes.sectionHeader}>
              <Typography variant="h6">Effective Permissions</Typography>
            </Box>
            <Box className={classes.sectionContent}>
              {userEffectivePermissionsLoading ? (
                <Typography variant="body1">Loading permissions...</Typography>
              ) : userEffectivePermissions.length > 0 ? (
                <Box className={classes.chipContainer}>
                  {userEffectivePermissions.map((permission, idx) => (
                    <Chip
                      key={idx}
                      label={permission}
                      size="small"
                      className={classes.chip}
                      color="primary"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body1">
                  No effective permissions found.
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addRoleDialogOpen}
        onClose={() => {
          setAddRoleDialogOpen(false);
          setSelectedRoleToAdd(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Add Role to {selectedUser?.name_surname}
          </Typography>

          <FormControl fullWidth>
            <Autocomplete
              value={selectedRoleToAdd}
              onChange={(e, newValue) => setSelectedRoleToAdd(newValue)}
              options={
                rolesData?.filter(
                  (role) => !role.is_superuser || currentUserIsSuperuser,
                ) || []
              }
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Role"
                  placeholder="Choose a role to assign"
                  InputLabelProps={{ shrink: true }}
                />
              )}
              renderOption={(option) => (
                <Box>
                  <Typography variant="body1">
                    {option.name}
                    {option.is_superuser && (
                      <Chip
                        label="Superuser"
                        color="primary"
                        size="small"
                        className={classes.superuserChip}
                      />
                    )}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {option.description}
                  </Typography>
                </Box>
              )}
            />
          </FormControl>

          {selectedRoleToAdd && (
            <Alert
              variant="outlined"
              severity={selectedRoleToAdd.is_superuser ? 'warning' : 'info'}
              className={classes.alertBox}
            >
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {selectedRoleToAdd.is_superuser ? 'Warning:' : 'Grants:'}
              </Typography>
              <Typography variant="body2">
                {selectedRoleToAdd.is_superuser
                  ? 'This role grants all permissions in the system (superuser access).'
                  : selectedRoleToAdd.permissions.length > 0
                  ? selectedRoleToAdd.permissions.join(', ')
                  : 'No specific permissions.'}
              </Typography>
            </Alert>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={() => {
              setAddRoleDialogOpen(false);
              setSelectedRoleToAdd(null);
            }}
            color="default"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignRole}
            color="primary"
            variant="contained"
            disabled={!selectedRoleToAdd || assigningRole}
          >
            {assigningRole ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={grantPermissionDialogOpen}
        onClose={() => {
          setGrantPermissionDialogOpen(false);
          setSelectedPermissionToGrant(null);
          setPermissionNote('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Grant Permission to {selectedUser?.name_surname}
          </Typography>

          <FormControl fullWidth>
            <Autocomplete
              value={selectedPermissionToGrant}
              onChange={(e, newValue) => setSelectedPermissionToGrant(newValue)}
              options={permissionsData || []}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Permission"
                  placeholder="Choose a permission to grant"
                  InputLabelProps={{ shrink: true }}
                />
              )}
              renderOption={(option) => (
                <Box>
                  <Typography variant="body1">{option.name}</Typography>
                  {option.description && (
                    <Typography variant="body2" color="textSecondary">
                      {option.description}
                    </Typography>
                  )}
                </Box>
              )}
            />
          </FormControl>

          <Box className={classes.noteField}>
            <TextField
              fullWidth
              variant="outlined"
              label="Note (Optional)"
              placeholder="Add a note for this permission grant"
              value={permissionNote}
              onChange={(e) => setPermissionNote(e.target.value)}
              multiline
              rows={3}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {selectedPermissionToGrant && (
            <Alert
              severity="info"
              variant="outlined"
              className={classes.alertBox}
            >
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                Grants:
              </Typography>
              <Typography variant="body2">
                {selectedPermissionToGrant.description ||
                  selectedPermissionToGrant.name}
              </Typography>
            </Alert>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={() => {
              setGrantPermissionDialogOpen(false);
              setSelectedPermissionToGrant(null);
              setPermissionNote('');
            }}
            color="default"
          >
            Cancel
          </Button>
          <Button
            onClick={handleGrantPermission}
            color="primary"
            variant="contained"
            disabled={!selectedPermissionToGrant || grantingPermission}
          >
            {grantingPermission ? 'Granting...' : 'Grant'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserManagementDialog;
